<template>
    <features-template-base
    operationsWrapper
    :nonAuthenticatedFeature="nonAuthenticatedFeature"
    :thirdLevelMenu="thirdLevelMenu"
    :hideClose="hideClose"
    :showStepper="showStepper && getMobileAppState"
    :applyFeedbackOperationStatusClass="feedbackOperationData?.status && !showStepper ? feedbackOperationData.status.toLowerCase() : null"
    :customBreadcrumbs="customBreadcrumbs"
    :showingFooter="showFooter"
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState"
    :errorStateMessage="errorStateMessage"
    :showFloatingButton="showFloatingButton"
    :floatingButton="floatingButton"
    :tokenTitleAppend="tokenTitleAppend"
    :tokenTitleFull="tokenTitleFull"
    :tokenParams="tokenParams"
    :tokenCustomUseParams="tokenCustomUseParams"
    :scrollValue="scrollValue"
    :showSearchSlot="showSearchSlot"
    :showTopRightAreaSlot="showTopRightAreaSlot"
    :customMobileBackIcon="customMobileBackIcon"
    @clearScrollToValue="emit('clearScrollToValue')"
    @floatingButtonClicked="emit('floatingButtonClicked')"
    @challengeCancelHandler="emit('challengeCancelHandler')"
    @challengeSuccessHandler="emit('challengeSuccessHandler', $event)"
    @challengeErrorHandler="emit('challengeErrorHandler', $event)"
    @mobileScrollValueUpdated="expandedFloatingButton = $event === 0"
    @scrollValueUpdated="expandedFloatingButton = $event === 0"
    @parentBreadcrumbClicked="emit('parentBreadcrumbClicked')"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <!-- Slot for elements position on top area of header in web, and on top of content in mobile -->
		<template v-if="showTopRightAreaSlot" #accounts-card-slot-area><slot name="accounts-card-slot-area"/></template>
        <!-- Slot for search in navigation-header -->
        <template v-if="showSearchSlot" #top-search-area-slot><slot name="top-search-area-slot"/></template>
        <!-- Stepper for mobile curtain on header -->
        <template v-if="showStepper && getMobileAppState" #top-stepper-area-slot>
            <stepper :activeStep="activeStep" :steps="stepperParams"/>
        </template>
        <!-- Content -->
        <transition-group name="common-enter-animation" mode="out-in" appear>
            <div
            v-if="!showLoader && !applyErrorState"
            ref="operationsWrapperRef"
            class="operations-feature-wrapper"
            :class="[{
                'visible-none': showLoader || hideContentWrapper,
                'with-footer-visible': showFooter,
                'mobile': getMobileAppState,
                'full-height': fullHeight,
                'apply-feedback-color': feedbackOperationData?.status && !showStepper,
                'non-authenticated-feature': nonAuthenticatedFeature,
                'with-floating-button': showFloatingButton && floatingButtonParams
            }, feedbackOperationData?.status.toLowerCase() ]">
                <!-- Feedback Operatio Component -->
                <feedback-operation
                v-if="feedbackOperationData && !showStepper" :feedbackOperationData="feedbackOperationData"/>
                <!-- Stepper -->
                <div v-if="showStepper" class="operation-content-stepper-wrapper" :class="{'full-width': stepperParams.length > 3}">
                    <stepper :activeStep="activeStep" :steps="stepperParams"/>
                </div>
                <div name="helper-element-to-align-stepper" class="to-help-align-with"/>
                <div class="helper-element-to-align-stepper" :class="{'full-size-content': fullSizeContent}">
                    <slot></slot>
                </div>
            </div>
        </transition-group>
        <!-- Empty-State-->
        <template #empty-state-slot><slot name="empty-state-slot"></slot></template>
        <!-- Floating Button -->
        <template #floating-button-area-slot>
            <!-- Floating Button -->
            <button-floating
            v-if="showFloatingButton && floatingButtonParams"
            :class="{'mobile': getMobileAppState, 'with-footer-visible': showFooter, 'running-in-ios': getDeviceInUse === 'ios'}"
            :extended="expandedFloatingButton"
            :icon="floatingButtonParams.icon"
            :label="floatingButtonParams.label"
            @clicked="emit('floatingButtonClicked')"/>
        </template>

        <template #footer-area-slot>
            <!-- Feature Footer -->
            <navigation-footer
            v-if="footerButtonsParams"
            :buttonsParams="footerButtonsParams"
            :showFooter="showFooter"
            :buttonFullWidth="buttonFullWidth"
            :footerWithInfoDisplayer="footerWithInfoDisplayer">
                <template v-if="footerWithInfoDisplayer" #footer-info-displayer><slot name="footer-info-displayer"></slot></template>
            </navigation-footer>
        </template>
    </features-template-base>
</template>

<script setup>
import FeaturesTemplateBase from '@/components/wrappers/featuresTemplates/Base'
import FeedbackOperation from '@/components/generic/feedbacks/Operation'
import Stepper from '@/components/generic/stepper/Stepper'
import ButtonFloating from '@/components/generic/buttons/Floating'
import NavigationFooter from '@/components/generic/navigation/Footer'
/* Vue */
import { ref, watch } from 'vue'
/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
const { updateFooterShowState } = mapActions()
const { getMobileAppState, getDeviceInUse } = mapGetters()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked', 'floatingButtonClicked', 'challengeCancelHandler', 'challengeSuccessHandler', 'challengeErrorHandler', 'clearScrollToValue', 'parentBreadcrumbClicked'])

/* Props
@nonAuthenticatedFeature {Boolean} prop to apply specific styles for this type of features
@thirdLevelMenu {Boolean}
@hideClose {Boolean} prop to prevent close button from being shown in nonAuthenticatedFeatures or mobile
@customBreadcrumbs {Object} - if our breadcrumbs are not direct from menu construction based on route, use this object
@scrollValue {Number} - new scrollValue position
@hideContentWrapper {Boolean} - to remove white box and paddings and hide content
@fullSizeContent {Boolean0} - prop to apply right styles for this view
@fullHeight {Boolean} - prop to apply full height
@showTopRightAreaSlot {Boolean}
@showSearchSlot {Boolean}
@showLoader {Boolean} - prop to show loader
@showOverlayLoader {Boolean} - prop to apply overlay loader
@applyErrorState {Boolean} - prop to apply Error State
@errorStateMessage {String} - message to display on errorState
@showStepper {Boolean} - prop to showStepper
@activeStep {Number} - prop with the current step number
@stepperParams {Array} - prop with list of steps, each item is an Object with:
    - icon  { String }
    - label { String/Number }
    - isOptional { Boolean }
@showFloatingButton {Boolean} - prop to trigger if floating button should be showned or not
@floatingButtonParams {Object} object with info for floating button
@buttonsParams { Array } each item is an Object
    - buttonType { String } primary or tertiary
    - buttonPosition { String } left or right
    - label { String }
    - icon { String }
    - disabled { Boolean }
    - callbackFunction { Function }
@showFooter { Boolean }
@buttonFullWidth { Boolean }
@footerWithInfoDisplayer {Boolean} - to show an info displayer on the top of the footer
@customMobileBackIcon {String} - name of custom icon for close mobile button
@tokenTitleAppend {String} - string to appentd at token modal title
@tokenTitleFull {String} - string to use as token modal title
@tokenParams {Object} - some times token submit will have additional params to send with token, when so, send it in this prop
@tokenCustomUseParams {Object} - when we want to have this modal but with apis diferent than the generic ones we send this object with:
    - messageToDisplay {String}
    - sendTokenAction {string} - 'moduleName/actionName'
    - resendTokenAction {string} - 'moduleName/actionName'
    - payload {Object} info that we want to send along with the response
    - resendPayload {Object} with info needed to resent token
    - forceResendChannelString {String} - ['email', 'sms'] - prop to ignore session token channel and use custom one
@feedbackOperationData {Object} - object with:
    - status {String} used for color and icon
    - title: {String} used to display in this component
    - buttonsData {Object} - info to create buttons and their login, object with
        emailAction {String} action name so component call it, example: 'sendOperationEmailNotification'
        smsAction {String} action name so component call it, example: 'sendOperationSMSNotification'
        sendPayload {Object} send actions payload
        downloadUrl {String} url string for download,
        operationId {String, Number} id used for shareLink, email/sms
*/
const props = defineProps({
    nonAuthenticatedFeature: Boolean,
    thirdLevelMenu: Boolean,
    hideClose: Boolean,
    customBreadcrumbs: Object,
    scrollValue: Number,
    hideContentWrapper: Boolean,
    fullSizeContent: Boolean,
    fullHeight: Boolean,
    showSearchSlot: Boolean,
    showTopRightAreaSlot: Boolean,
    /* Loader Props */
    showLoader: Boolean,
    showOverlayLoader: Boolean,
    /* ErrorState Props */
    applyErrorState: Boolean,
    errorStateMessage: String,
    /* Stepper Props */
    showStepper: Boolean,
    activeStep: Number,
    stepperParams: Array,
    /* Floating Button Props */
    showFloatingButton: Boolean,
    floatingButtonParams: Object,
    /* Footer Props */
    showFooter: Boolean,
    buttonFullWidth: Boolean,
    footerButtonsParams: Array,
    footerWithInfoDisplayer: Boolean,
    customMobileBackIcon: String,
    /* Token related Props */
    tokenTitleAppend: String,
    tokenTitleFull: String,
    tokenParams: Object,
    tokenCustomUseParams: Object,
    /* FeedbackOperationData */
    feedbackOperationData: Object
})

const expandedFloatingButton = ref(true)
const operationsWrapperRef = ref('')

watch(() => props.showFooter, (newVal, oldVal) => {
    updateFooterShowState(props.showFooter)
})

updateFooterShowState(props.showFooter)
</script>

<style lang="scss" scoped>
.operations-feature-wrapper {
    @include commonEnteringAnimation;
    @include cardGeneral1;
    max-width: 1168px;
    margin: 0 auto;
    padding: 0 0 32px 0;
    z-index: 1;

    position: relative;

    &.visible-none {
        opacity: 0;
        height: 0;
        padding: 0;

        &.with-footer-visible {
            margin: 0;
        }
    }

    &.with-footer-visible {
        margin-bottom: 80px;
    }

    &.non-authenticated-feature {
        max-width: 100%;
    }

    .operation-content-stepper-wrapper {
        width: fit-content;
        height: fit-content;

        margin: 32px auto 56px auto;

        flex-shrink: 0;

        overflow: hidden;

        &.full-width {
            width: 100%;
            margin: 32px 0 56px 0;

            &:before, &:after {
                content: '';
                width: 47px;
                height: 89px;

                background-image: linear-gradient(90deg, var(--secondary-light-4) 0%, rgba(255, 255, 255, 0.00) 100%);
                position: absolute;
                top: 32px;
                z-index: 1;
            }

            &:before {
                left: 0;
            }

            &:after {
                right: 0;
                background-image: linear-gradient(270deg, var(--secondary-light-4) 0%, rgba(255, 255, 255, 0.00) 100%);
            }
        }

    }

    .helper-element-to-align-stepper, .to-help-align-with {
        width: 508px;
        max-width: calc(100% - 32px);
        align-self: center;

        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 24px;
    }

    .helper-element-to-align-stepper.full-size-content {
        width: 100%;
        max-width: 100%;
        padding: 0 24px;

        align-items: center;
    }

    &.full-height {
        min-height: calc(100% - 24px);

        &.with-footer-visible {
            min-height: calc(100% - 80px - 24px);
        }

        .helper-element-to-align-stepper {
            height: 100%;
        }
    }

    &.mobile {
        padding-bottom: 0;
        background-color: var(--secondary-light-3);

        &.apply-feedback-color {

            .feedback-operation-wrapper {
                margin-top: -2px;
            }

            &.info, &.success {
                background-image: linear-gradient(var(--positive) 40%, var(--secondary-light-4) 10%);
            }

            &.error {
                background-image: linear-gradient(var(--negative) 40%, var(--secondary-light-4) 10%);
            }

            &.warning {
                background-image: linear-gradient(var(--warning) 40%, var(--secondary-light-4) 10%);
            }
        }

        .operation-content-stepper-wrapper {
            padding: 0 12px; // 16 - 4px from step
            margin: 12px 0 8px 0; // 16 - 4px from step, 0, 12 - 4px from step, 0

            &:before, &:after {
                display: none;
            }
        }

        .helper-element-to-align-stepper {
            width: 100%;
            max-width: 100%;
            padding: 16px 0 0 0;

            background-color: var(--secondary-light-4);
            border-radius: 16px 16px 0px 0px;

            /* Some browsers ignore padding-bottom when no elements are render after it, we use an after to force this space */
            &:after {
                content: '_';
                width: 100%;
                height: 0;

                visibility: hidden;
                display: inline-block;
            }

            > :deep(*) {
                &:not(.prevent-auto-margins) {
                    width: calc(100% - 32px);
                    margin-left: auto;
                    margin-right: auto;
                }
            }
        }

        &.full-height {
            min-height: 100%;

            &.with-footer-visible {
                min-height: calc(100% - 72px);
            }
        }

        &.with-footer-visible {
            margin-bottom: 0;

            .helper-element-to-align-stepper {
                /* Some browsers ignore padding-bottom when no elements are render after it, we use an after to force this space */
                &:after {
                    content: '_';
                    width: 100%;
                    height: 80px;

                    visibility: hidden;
                    display: inline-block;
                }
            }

            &.with-floating-button {
                .helper-element-to-align-stepper {
                    &:after {
                        height: 128px;
                    }
                }
            }
        }
    }
}

.button-floating {
    position: absolute;
    bottom: 24px;
    right: 24px;
    z-index: 1;

    &.mobile {
        bottom: 16px;
        right: 16px;

        &.running-in-ios {
            bottom: calc(16px + constant(safe-area-inset-bottom)); /* iOS 11.0 */
            bottom: calc(16px + env(safe-area-inset-bottom)); /* iOS 11.2 */
        }
    }

    &.with-footer-visible {
        bottom: 104px;

        &.mobile {
            bottom: 96px;

            &.running-in-ios {
                bottom: calc(96px + constant(safe-area-inset-bottom)); /* iOS 11.0 */
                bottom: calc(96px + env(safe-area-inset-bottom)); /* iOS 11.2 */
            }
        }
    }
}
</style>
